/**
 * Scheduled maintenance notice, delivered through Firebase Remote Config —
 * deliberately outside our own servers, so the notice and the block screen keep
 * working while the backend is being upgraded or is down.
 *
 * Remote Config parameters (same template as the mobile app), suffixed per
 * environment outside production (see {@link getMaintenanceParameterKeys}):
 * - `maintenance_enabled` (boolean): manual kill switch, blocks the app.
 * - `maintenance_schedule` (JSON string): see {@link MaintenanceSchedule}.
 * - `maintenance_bypass_code` (string): lets testers past the block screen on
 *   their device. A convenience, not security: the code reaches every client.
 *
 * Texts are keyed by locale (`en-GB`, `en-US`, `de-DE`) or by language (`en`,
 * `de`). `upcoming` / `in_progress` hold the texts for one phase; the top-level
 * `title` / `message` are used for any phase without texts of its own.
 *
 * Kept identical across client, id, manager and admin; only the Firebase
 * adapter (`remoteConfig.ts`) differs per app.
 */

export type MaintenanceApp = "client" | "client-native" | "id" | "manager" | "admin";

export type MaintenancePhase = "none" | "upcoming" | "inProgress";

/** Locale or language code → text. `{start}`, `{end}` and `{timezone}` are filled in on display. */
export type MaintenanceTexts = Record<string, string>;

export type MaintenancePhaseTexts = {
  title: MaintenanceTexts;
  message: MaintenanceTexts;
};

export type MaintenanceSchedule = {
  id: string;
  announceFrom: Date | null;
  startsAt: Date;
  endsAt: Date | null;
  severity: "info" | "warning";
  dismissible: boolean;
  blockDuring: boolean;
  /** Shared texts, used for a phase without texts of its own. */
  title: MaintenanceTexts;
  message: MaintenanceTexts;
  /** Before `startsAt`. */
  upcoming: MaintenancePhaseTexts;
  /** Inside the window: the banner, or the block screen when `blockDuring`. */
  inProgress: MaintenancePhaseTexts;
  link: string | null;
  /** Apps that show the schedule; empty means all (including `mobile`). */
  apps: string[];
};

export type MaintenanceConfig = {
  enabled: boolean;
  schedule: MaintenanceSchedule | null;
  /** Empty when no bypass is offered. */
  bypassCode: string;
};

export type MaintenanceParameterKeys = {
  enabled: string;
  schedule: string;
  bypassCode: string;
};

const normalizeEnvironment = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const environmentFromHost = (host: string): string => {
  const stage = host.match(/(?:^|[.-])(stage-[a-z0-9]+)(?:[.-]|$)/);
  if (stage) return normalizeEnvironment(stage[1]);
  if (/^(localhost|127\.0\.0\.1|0\.0\.0\.0)$|\.localhost$|\.local$/.test(host)) return "local";
  if (/(?:^|[.-])staging(?:[.-]|$)/.test(host)) return "staging";
  if (/(?:^|[.-])dev(?:[.-]|$)/.test(host)) return "dev";
  return "";
};

/**
 * Which Remote Config environment this deployment reads, so every stage can be
 * tested on its own: "" is production, otherwise e.g. "stage_b" or "local".
 * An explicit override wins; otherwise it is derived from the backend URL
 * (`stage-b-api.tournated.com` → "stage_b"). Anything unrecognised counts as
 * production, so a production deployment can never miss a production notice.
 */
export const resolveMaintenanceEnvironment = (
  override: string | null | undefined,
  backendUrl: string | null | undefined
): string => {
  if (override?.trim()) {
    const normalized = normalizeEnvironment(override);
    return normalized === "production" || normalized === "prod" ? "" : normalized;
  }
  if (!backendUrl) return "";
  try {
    return environmentFromHost(new URL(backendUrl).hostname.toLowerCase());
  } catch {
    return "";
  }
};

/** `maintenance_enabled` / `maintenance_schedule`, suffixed with `_<environment>` outside production. */
export const getMaintenanceParameterKeys = (environment: string): MaintenanceParameterKeys => {
  const suffix = environment ? `_${environment}` : "";
  return {
    enabled: `maintenance_enabled${suffix}`,
    schedule: `maintenance_schedule${suffix}`,
    bypassCode: `maintenance_bypass_code${suffix}`,
  };
};

const parseDate = (value: unknown): Date | null => {
  if (typeof value !== "string") return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const parseTexts = (value: unknown): MaintenanceTexts => {
  if (typeof value === "string") return value.trim() ? { en: value } : {};
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string" && !!entry[1].trim()
    )
  );
};

const parsePhaseTexts = (value: unknown): MaintenancePhaseTexts => {
  const data = isRecord(value) ? value : {};
  return { title: parseTexts(data.title), message: parseTexts(data.message) };
};

/** Null for an empty value or invalid JSON: a console typo hides the notice instead of breaking the app. */
export const parseMaintenanceSchedule = (raw: string): MaintenanceSchedule | null => {
  if (!raw.trim()) return null;
  try {
    const data: unknown = JSON.parse(raw);
    if (!isRecord(data)) return null;
    const startsAt = parseDate(data.starts_at);
    if (typeof data.id !== "string" || !data.id || !startsAt) return null;
    return {
      id: data.id,
      announceFrom: parseDate(data.announce_from),
      startsAt,
      endsAt: parseDate(data.ends_at),
      severity: data.severity === "warning" ? "warning" : "info",
      dismissible: data.dismissible !== false,
      blockDuring: data.block_during === true,
      title: parseTexts(data.title),
      message: parseTexts(data.message),
      upcoming: parsePhaseTexts(data.upcoming),
      inProgress: parsePhaseTexts(data.in_progress),
      link: typeof data.link === "string" && data.link ? data.link : null,
      apps: Array.isArray(data.apps)
        ? data.apps.filter((app): app is string => typeof app === "string")
        : [],
    };
  } catch {
    return null;
  }
};

export const getMaintenancePhase = (
  schedule: MaintenanceSchedule,
  app: MaintenanceApp,
  now: Date
): MaintenancePhase => {
  if (schedule.apps.length && !schedule.apps.includes(app)) return "none";
  if (schedule.endsAt && now >= schedule.endsAt) return "none";
  if (now >= schedule.startsAt) return "inProgress";
  if (schedule.announceFrom && now < schedule.announceFrom) return "none";
  return "upcoming";
};

/** Next moment the phase can change, to schedule a re-render. */
export const getNextBoundary = (schedule: MaintenanceSchedule, now: Date): Date | null =>
  [schedule.announceFrom, schedule.startsAt, schedule.endsAt]
    .filter((date): date is Date => !!date && date > now)
    .sort((a, b) => a.getTime() - b.getTime())[0] ?? null;

// Legacy app codes that are not BCP 47 languages.
const LANGUAGE_ALIASES: Record<string, string> = { cn: "zh", ua: "uk" };

const toLocaleTag = (value: string): string => {
  const [language = "", ...rest] = value.trim().replace(/_/g, "-").split("-");
  return [LANGUAGE_ALIASES[language.toLowerCase()] ?? language, ...rest].join("-");
};

const languageOf = (value: string): string => toLocaleTag(value).split("-")[0].toLowerCase();

const MAINTENANCE_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZoneName: "short",
};

const formatWith = (locale: string, options: Intl.DateTimeFormatOptions, date: Date): string => {
  try {
    return new Intl.DateTimeFormat(toLocaleTag(locale), options).format(date);
  } catch {
    return new Intl.DateTimeFormat("en", options).format(date);
  }
};

/** Date and time in the viewer's own timezone, with its name: "2 Nov 2026, 23:00 GMT+1". */
export const formatMaintenanceTime = (date: Date, locale: string): string =>
  formatWith(locale, MAINTENANCE_TIME_FORMAT, date);

/** The viewer's timezone in words, e.g. "Central European Standard Time". */
export const formatMaintenanceTimeZone = (date: Date, locale: string): string => {
  const format = (tag: string) =>
    new Intl.DateTimeFormat(tag, { timeZoneName: "long" })
      .formatToParts(date)
      .find((part) => part.type === "timeZoneName")?.value ?? "";
  try {
    return format(toLocaleTag(locale));
  } catch {
    return format("en");
  }
};

type PickedText = { text: string; locale: string };

/**
 * The text for the viewer's locale: the exact locale (`en-US`), then its
 * language (`en`), then another locale of that language (`en-GB`), then English,
 * then anything. Dates are formatted in the language the text is written in, so
 * a German viewer reading the English fallback never gets German dates.
 */
const pickText = (texts: MaintenanceTexts, locale: string): PickedText | null => {
  const entries = Object.entries(texts);
  if (!entries.length) return null;
  const wanted = toLocaleTag(locale).toLowerCase();
  const language = languageOf(locale);
  const find = (matches: (key: string) => boolean) =>
    entries.find(([key]) => matches(toLocaleTag(key).toLowerCase()));
  const [key, text] =
    find((key) => key === wanted) ??
    find((key) => key === language) ??
    find((key) => languageOf(key) === language) ??
    find((key) => key === "en") ??
    find((key) => languageOf(key) === "en") ??
    entries[0];
  // The viewer's own regional format wins within the same language (en-US dates for an en-GB
  // text); otherwise dates follow the text, so they never mix two languages.
  const hasRegion = toLocaleTag(locale).includes("-");
  return {
    text,
    locale: languageOf(key) === language && hasRegion ? locale : toLocaleTag(key),
  };
};

const textsFor = (
  schedule: MaintenanceSchedule,
  phase: Exclude<MaintenancePhase, "none">,
  field: keyof MaintenancePhaseTexts
): MaintenanceTexts => {
  const own = (phase === "inProgress" ? schedule.inProgress : schedule.upcoming)[field];
  return Object.keys(own).length ? own : schedule[field];
};

/**
 * The configured title or message for a phase in the viewer's locale, with
 * `{start}`, `{end}` and `{timezone}` filled in. Null when nothing is configured,
 * so the app shows its own translated default.
 */
export const localizeMaintenanceText = (
  schedule: MaintenanceSchedule,
  phase: Exclude<MaintenancePhase, "none">,
  field: keyof MaintenancePhaseTexts,
  locale: string
): string | null => {
  const picked = pickText(textsFor(schedule, phase, field), locale);
  if (!picked) return null;
  return picked.text
    .replaceAll("{start}", formatMaintenanceTime(schedule.startsAt, picked.locale))
    .replaceAll(
      "{end}",
      schedule.endsAt ? formatMaintenanceTime(schedule.endsAt, picked.locale) : ""
    )
    .replaceAll("{timezone}", formatMaintenanceTimeZone(schedule.startsAt, picked.locale));
};

/** Where a device keeps the bypass code it was let in with (the same key in every app). */
export const MAINTENANCE_BYPASS_STORAGE_KEY = "tournated:maintenance-bypass";

/** Whether `code` is the configured bypass code. Never true when no code is configured. */
export const matchesMaintenanceBypassCode = (config: MaintenanceConfig, code: string | null) =>
  !!config.bypassCode && code?.trim() === config.bypassCode;
